/**
 * ProjectDetailsMobileStats Component
 *
 * Collapsible one-liner stats bar for mobile:
 * - Collapsed: "75% · 5/10 tasks · 7 days left"
 * - Expanded: Full stats cards (same as desktop)
 * - Tap to toggle
 */

import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Progress } from 'antd';
import { link, border } from '../../configs/designTokens';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  DownOutlined,
  UpOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import '../../styles/index.scss';

const ProjectDetailsMobileStats = ({ project }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const stats = useMemo(() => {
    if (!project || !project.tasks) {
      return {
        totalTasks: 0,
        completedTasks: 0,
        completionPercentage: 0,
        daysLeft: null,
        isOverdue: false,
      };
    }

    const totalTasks = project.tasks.length;
    const completedTasks = project.tasks.filter(
      (task) => task.status === 'COMPLETED' || task.status === 'DONE'
    ).length;
    const completionPercentage = totalTasks > 0
      ? Math.round((completedTasks / totalTasks) * 100)
      : 0;

    let daysLeft = null;
    let isOverdue = false;
    if (project.endDate) {
      const today = moment();
      const endDate = moment(project.endDate);
      daysLeft = endDate.diff(today, 'days');
      isOverdue = daysLeft < 0;
    }

    return {
      totalTasks,
      completedTasks,
      completionPercentage,
      daysLeft,
      isOverdue,
    };
  }, [project]);

  const getDaysText = () => {
    if (stats.daysLeft === null) return 'No deadline';
    if (stats.isOverdue) return `${Math.abs(stats.daysLeft)}d overdue`;
    if (stats.daysLeft === 0) return 'Due today';
    if (stats.daysLeft === 1) return '1 day left';
    return `${stats.daysLeft}d left`;
  };

  return (
    <div className="project-mobile-stats">
      {/* Collapsed View - One-liner */}
      <div
        className={`project-mobile-stats__summary ${isExpanded ? 'project-mobile-stats__summary--hidden' : ''}`}
        onClick={() => setIsExpanded(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            setIsExpanded(true);
          }
        }}
      >
        <div className="project-mobile-stats__summary-content">
          {/* Progress indicator */}
          <div className="project-mobile-stats__progress-mini">
            <div
              className="project-mobile-stats__progress-fill"
              style={{ width: `${stats.completionPercentage}%` }}
            />
          </div>

          {/* Stats text */}
          <span className="project-mobile-stats__text">
            <span className="project-mobile-stats__text-highlight">
              {stats.completionPercentage}%
            </span>
            <span className="project-mobile-stats__text-sep">·</span>
            <span>{stats.completedTasks}/{stats.totalTasks} tasks</span>
            <span className="project-mobile-stats__text-sep">·</span>
            <span className={stats.isOverdue ? 'project-mobile-stats__text-danger' : ''}>
              {getDaysText()}
            </span>
          </span>
        </div>

        <DownOutlined className="project-mobile-stats__expand-icon" />
      </div>

      {/* Expanded View - Full Stats */}
      <div className={`project-mobile-stats__expanded ${isExpanded ? 'project-mobile-stats__expanded--visible' : ''}`}>
        <div
          className="project-mobile-stats__collapse-btn"
          onClick={() => setIsExpanded(false)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setIsExpanded(false);
            }
          }}
        >
          <span>Project Stats</span>
          <UpOutlined />
        </div>

        <div className="project-mobile-stats__cards">
          {/* Completion Card */}
          <div className="project-mobile-stats__card">
            <div className="project-mobile-stats__card-icon project-mobile-stats__card-icon--primary">
              <CheckCircleOutlined />
            </div>
            <div className="project-mobile-stats__card-content">
              <span className="project-mobile-stats__card-value">
                {stats.completionPercentage}%
              </span>
              <span className="project-mobile-stats__card-label">Complete</span>
            </div>
            <Progress
              percent={stats.completionPercentage}
              showInfo={false}
              strokeColor={link.blue}
              trailColor={border.slate}
              size="small"
              className="project-mobile-stats__card-progress"
            />
          </div>

          {/* Tasks Card */}
          <div className="project-mobile-stats__card">
            <div className="project-mobile-stats__card-icon project-mobile-stats__card-icon--success">
              <ClockCircleOutlined />
            </div>
            <div className="project-mobile-stats__card-content">
              <span className="project-mobile-stats__card-value">
                {stats.completedTasks}/{stats.totalTasks}
              </span>
              <span className="project-mobile-stats__card-label">Tasks Done</span>
            </div>
          </div>

          {/* Days Left Card */}
          <div className={`project-mobile-stats__card ${stats.isOverdue ? 'project-mobile-stats__card--danger' : ''}`}>
            <div className={`project-mobile-stats__card-icon ${stats.isOverdue ? 'project-mobile-stats__card-icon--danger' : 'project-mobile-stats__card-icon--warning'}`}>
              <CalendarOutlined />
            </div>
            <div className="project-mobile-stats__card-content">
              <span className={`project-mobile-stats__card-value ${stats.isOverdue ? 'project-mobile-stats__card-value--danger' : ''}`}>
                {stats.daysLeft !== null ? Math.abs(stats.daysLeft) : 'N/A'}
              </span>
              <span className="project-mobile-stats__card-label">
                {stats.isOverdue ? 'Days Overdue' : 'Days Left'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ProjectDetailsMobileStats.propTypes = {
  project: PropTypes.shape({
    tasks: PropTypes.arrayOf(
      PropTypes.shape({
        status: PropTypes.string,
      })
    ),
    endDate: PropTypes.string,
  }),
};

ProjectDetailsMobileStats.defaultProps = {
  project: null,
};

export default ProjectDetailsMobileStats;
